import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, ChevronLeft, ChevronRight, ChevronsRight, Lock } from "lucide-react";
import CalendarIcon from "app/assets/IconComponent/CalendarIcon";
import CandleCake from "app/assets/IconComponent/CandleCake";
import ClockIcon from "app/assets/IconComponent/ClockIcon";
import LockIcon from "app/assets/IconComponent/LockIcon";

export type YearStepStatus = "COMPLETED" | "SKIPPED" | "CURRENT" | "LOCKED";

export interface YearStep {
  yearNumber: number;
  calendarYear?: number;
  status: YearStepStatus;
  label?: string;
  completed?: boolean;
}

interface YearStepperProps {
  steps: YearStep[];
  onYearClick?: (step: YearStep) => void;
  mode?: "overlay" | "inline" | "navbar";
  phases?: string[];
  selectedPhase?: string;
  onPhaseClick?: (phase: string) => void;
  selectedYear?: any;
  selectedYearNumber?: number;
  allowLockedClick?: boolean;
  isDark?: boolean;
}

const StepIcon = ({ status, completed }: { status: YearStepStatus; completed?: boolean }) => {
  const inner = "w-[34px] h-[34px] rounded-full flex items-center justify-center";

  switch (status) {
    case "COMPLETED":
      if (completed) {
        // All confirmed — show clock (same as CURRENT style)
        return (
          <div className="p-1 rounded-full bg-[#1DA750]">
            <div className={`${inner} bg-[#1DA750] shadow-sm !border-[#fff] border-2`}>
              <Check size={18} strokeWidth={3} color="#fff" />
            </div>
          </div>
        );
      }
      // Partial — lighter green, no white border
      return (
        <div className="p-1 rounded-full" style={{ backgroundColor: "rgba(29, 167, 80, 0.2)" }}>
          <div className={`${inner} bg-[#1DA750] shadow-sm `}>
            <Check size={18} strokeWidth={3} color="#fff" />
          </div>
        </div>
      );
    case "SKIPPED":
      return (
        <div className="p-1 rounded-full" style={{ backgroundColor: "rgba(209, 213, 220, 0.2)" }}>
          <div className="rounded-full p-[4px] bg-[#E5E7EB]">
            <div className="w-[32px] h-[32px] rounded-full flex items-center justify-center bg-[#D1D5DD] border-2 border-white">
              <ChevronsRight size={18} strokeWidth={2.5} color="#FFF" />
            </div>
          </div>
        </div>
      );
    case "CURRENT":
      return (
        <div className="p-1 rounded-full" style={{ backgroundColor: "rgba(0,71,129,0.2)" }}>
          <div className={`${inner} bg-[#004781] shadow-sm`}>
            <ClockIcon />
          </div>
        </div>
      );
    case "LOCKED":
      return (
        <div className="p-1 rounded-full" style={{ backgroundColor: "rgba(108, 117, 125, 0.34)" }}>
          <div className={`${inner} bg-[#6C757D]`}>
            <LockIcon />
          </div>
        </div>
      );
    default:
      return null;
  }
};

/** Small colored dot used inside the navbar year dropdown — mirrors StepIcon. */
const StatusDot = ({ status, completed }: { status: YearStepStatus; completed?: boolean }) => {
  const base = "flex items-center justify-center w-4 h-4 rounded-full flex-shrink-0";
  switch (status) {
    case "COMPLETED":
      if (completed) {
        return (
          <span className={`${base} bg-[#004781]`}>
            <ClockIcon width={8} />
          </span>
        );
      }
      return (
        <span className={`${base} bg-[#1DA750]/60`}>
          <Check size={8} strokeWidth={3} color="#fff" />
        </span>
      );
    case "SKIPPED":
      return (
        <span className={`${base} bg-[#D1D5DD]`}>
          <ChevronsRight size={8} strokeWidth={2} color="#9CA3AF" />
        </span>
      );
    case "CURRENT":
      return (
        <span className={`${base} bg-[#004781]`}>
          <ClockIcon width={8} />
        </span>
      );
    case "LOCKED":
      return (
        <span className={`${base} bg-[#E5E7EB]`}>
          <Lock size={7} strokeWidth={2} color="#9CA3AF" />
        </span>
      );
    default:
      return null;
  }
};

const CONNECTOR_MT = "mt-[34px]";

const YearStepper = ({
  steps,
  onYearClick,
  mode = "overlay",
  phases,
  selectedYear,
  selectedYearNumber,
  allowLockedClick = false,
  selectedPhase,
  onPhaseClick,
  isDark = false,
}: YearStepperProps) => {
  const [showCalendarYear, setShowCalendarYear] = useState(false);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [phaseDropdownOpen, setPhaseDropdownOpen] = useState(false);
  const [selectedInlineYear, setSelectedInlineYear] = useState<number | null>(null);
  const [localSelectedStep, setLocalSelectedStep] = useState<YearStep | null>(null);
  const [localSelectedPhase, setLocalSelectedPhase] = useState<string | null>(null);
  const [overlayWindowStart, setOverlayWindowStart] = useState(0);
  const navbarRef = useRef<HTMLDivElement>(null);
  const lastClickRef = useRef<{ yearNumber: number; time: number } | null>(null);

  useEffect(() => {
    if (mode !== "navbar") return;
    const handleClickOutside = (e: MouseEvent) => {
      if (navbarRef.current && !navbarRef.current.contains(e.target as Node)) {
        setYearDropdownOpen(false);
        setPhaseDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mode]);

  useEffect(() => {
    if (mode !== "overlay" || !steps?.length) return;
    const activeYearNum =
      selectedYearNumber ??
      steps.find((s) => s.status === "CURRENT")?.yearNumber ??
      steps[0]?.yearNumber;
    if (activeYearNum == null) return;
    const idx = steps.findIndex((s) => s.yearNumber === activeYearNum);
    if (idx === -1) return;
    setOverlayWindowStart((prev) => {
      if (idx >= prev && idx < prev + 5) return prev;
      return Math.max(0, Math.min(idx - 2, Math.max(0, steps.length - 5)));
    });
    // eslint-disable-next-line
  }, [selectedYearNumber, mode]);

  const currentStep =
    (selectedYearNumber != null ? steps?.find((s) => s.yearNumber === selectedYearNumber) : null) ??
    steps?.find((s) => s.status === "CURRENT") ??
    selectedYear ??
    steps?.[0];
  const activeStep = (localSelectedStep ?? currentStep) as YearStep | undefined;
  const activePhase = localSelectedPhase ?? selectedPhase ?? phases?.[0];

  const getYearLabel = (step: YearStep) =>
    showCalendarYear && step.calendarYear != null
      ? `${step.calendarYear}`
      : `YR ${step.yearNumber}`;

  const calendarToggle = (
    <button
      type="button"
      className={`flex items-center justify-center rounded-full border transition-colors flex-shrink-0 absolute left-[8px] top-[8px] ${
        isDark
          ? "bg-[#2A2A3D] border-[#3A3A50] hover:border-[#6B7280]"
          : "bg-white border-[#D1D5DB] hover:border-[#9CA3AF]"
      }`}
      onClick={() => setShowCalendarYear((v) => !v)}
      title={showCalendarYear ? "Switch to Plan Year" : "Switch to Calendar Year"}
    >
      {showCalendarYear ? (
        <CandleCake fill={isDark ? "#9CA3AF" : "#004781"} width={25} height={25} />
      ) : (
        <CalendarIcon fill={isDark ? "#9CA3AF" : "#004781"} width={25} height={25} />
      )}
    </button>
  );
  const phaseLabel = (phase: string): any => {
    if (phase === "postCredit") return <span className="font-bold conthrax-font">Post-Credit</span>;
    return <span className="font-bold conthrax-font">Pre-Credit</span>;
  };

  if (mode === "navbar") {
    const hasSteps = steps?.length > 0;

    const panelBg = isDark ? "bg-[#1E1E29]" : "bg-white";
    const panelBorder = isDark ? "border-[#3A3A50]" : "border-[#E5E7EB]";
    const dividerBg = isDark ? "bg-[#3A3A50]" : "bg-[#E5E7EB]";
    const reviewLabelColor = isDark ? "#9CA3AF" : "#6C757D";
    const yearBtnColor = isDark ? "#93C5FD" : "#1E3A5F";
    const phaseBtnColor = isDark ? "#E5E7EB" : "#374151";
    const chevronPhaseColor = isDark ? "#9CA3AF" : "#6B7280";
    const optionActiveClass = isDark
      ? "bg-[#1E3A8A] text-white font-semibold"
      : "bg-[#EFF6FF] text-[#1E3A5F] font-semibold";
    const optionIdleClass = isDark
      ? `text-[#E5E7EB] hover:bg-[#2A2A3D]`
      : "text-[#374151] hover:bg-[#F9FAFB]";

    const phaseDropdown = phases && phases.length > 0 && (
      <>
        <div className={`w-px h-4 ${dividerBg}`} />
        <div className="relative whitespace-nowrap">
          <button
            type="button"
            className="flex items-center gap-[5px] text-[13px] font-medium hover:opacity-80 transition-opacity"
            style={{ color: phaseBtnColor }}
            onClick={() => {
              setPhaseDropdownOpen((v) => !v);
              setYearDropdownOpen(false);
            }}
          >
            {phaseLabel(activePhase ?? phases[0])}
            <ChevronDown
              size={13}
              strokeWidth={2}
              color={chevronPhaseColor}
              style={{
                transition: "transform 0.15s",
                transform: phaseDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </button>

          {phaseDropdownOpen && (
            <div
              className={`absolute top-full left-0 mt-2 ${panelBg} rounded-lg shadow-lg border ${panelBorder} z-50 min-w-[150px] py-1 overflow-hidden whitespace-normal`}
            >
              {phases?.map((phase) => (
                <button
                  key={phase}
                  type="button"
                  className={`w-full text-left px-3 py-[7px] text-[12px] transition-colors ${
                    phase === (activePhase ?? phases[0]) ? optionActiveClass : optionIdleClass
                  }`}
                  onClick={() => {
                    setLocalSelectedPhase(phase);
                    onPhaseClick?.(phase);
                    setPhaseDropdownOpen(false);
                  }}
                >
                  {phaseLabel(phase)}
                </button>
              ))}
            </div>
          )}
        </div>
      </>
    );

    return (
      <div
        ref={navbarRef}
        className={`${panelBg} rounded-full px-3 py-[5px] shadow-sm border ${panelBorder}`}
      >
        <div className="flex items-center gap-3">
          {hasSteps && currentStep ? (
            <div className="flex flex-col">
              <span className="text-[10px] conthrax-font" style={{ color: reviewLabelColor }}>
                Review
              </span>
              <div className="relative whitespace-nowrap">
                <button
                  type="button"
                  className="flex items-center gap-[5px] text-[13px] font-semibold conthrax-font hover:opacity-80 transition-opacity"
                  style={{ color: yearBtnColor }}
                  onClick={() => {
                    setYearDropdownOpen((v) => !v);
                    setPhaseDropdownOpen(false);
                  }}
                >
                  {getYearLabel(activeStep ?? steps?.[0])}
                  <ChevronDown
                    size={13}
                    strokeWidth={2.5}
                    color={yearBtnColor}
                    style={{
                      transition: "transform 0.15s",
                      transform: yearDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                </button>

                {yearDropdownOpen && (
                  <div
                    className={`absolute top-full left-0 mt-2 ${panelBg} rounded-lg shadow-lg border ${panelBorder} z-50 min-w-[130px] py-1 overflow-hidden`}
                  >
                    {steps.map((step) => {
                      const isLocked = step.status === "LOCKED" && !allowLockedClick;
                      const isCurr = step.yearNumber === activeStep?.yearNumber;
                      return (
                        <button
                          key={step.yearNumber}
                          type="button"
                          className={`w-full text-left px-3 py-[7px] text-[12px] flex items-center gap-2 transition-colors ${
                            isCurr
                              ? optionActiveClass
                              : isLocked
                              ? "text-[#9CA3AF] cursor-not-allowed"
                              : `${optionIdleClass} cursor-pointer`
                          }`}
                          onClick={() => {
                            if (!isLocked) {
                              setLocalSelectedStep(step);
                              onYearClick?.(step);
                              setYearDropdownOpen(false);
                            }
                          }}
                          disabled={isLocked}
                        >
                          <StatusDot status={step.status} completed={step.completed} />
                          <span className="conthrax-font">{getYearLabel(step)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <span className="text-[13px] text-[#9CA3AF] conthrax-font">—</span>
          )}
          {phaseDropdown}
        </div>
      </div>
    );
  }

  if (mode === "inline") {
    if (!steps?.length) return null;

    const activeYear =
      selectedInlineYear ??
      (selectedYearNumber != null ? selectedYearNumber : null) ??
      steps.find((s) => s.status === "CURRENT")?.yearNumber ??
      steps[0]?.yearNumber;

    const statusColor = (s: YearStepStatus) =>
      s === "COMPLETED" ? "#1DA750" : s === "CURRENT" ? "#1E3A5F" : "#9CA3AF";

    return (
      <div className="w-full bg-white dark:bg-[#2A2A3D] rounded-xl shadow-sm border border-[#E5E7EB] dark:border-[#3A3A50] flex items-stretch overflow-x-auto">
        <div className="flex items-center px-3 border-r border-[#E5E7EB] dark:border-[#3A3A50] flex-shrink-0">
          <button
            type="button"
            className={`flex items-center justify-center w-[26px] h-[26px] rounded-full border transition-colors `}
            onClick={() => setShowCalendarYear((v) => !v)}
            title={showCalendarYear ? "Switch to Plan Year" : "Switch to Calendar Year"}
          >
            <CalendarIcon width={13} color={showCalendarYear ? "#fff" : "#9CA3AF"} />
          </button>
        </div>

        {/* Tabs */}
        {steps.map((step) => {
          const isActive = step.yearNumber === activeYear;
          const isClickable = step.status !== "LOCKED" || allowLockedClick;
          const color = statusColor(step.status);

          return (
            <button
              key={step.yearNumber}
              type="button"
              disabled={!isClickable}
              onClick={() => {
                if (!isClickable) return;
                setSelectedInlineYear(step.yearNumber);
                onYearClick?.(step);
              }}
              className={`relative flex flex-col items-center justify-center px-5 py-2.5 min-w-[80px] border-b-2 transition-all flex-shrink-0 ${
                isActive ? "border-[#1581ED]" : "border-transparent hover:border-[#CBD5E1]"
              } ${!isClickable ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
            >
              <span
                className={`text-[11px] font-bold conthrax-font leading-none ${
                  isActive ? "text-[#1E3A5F] dark:text-[#93C5FD]" : "text-[#6B7280]"
                }`}
              >
                {getYearLabel(step)}
              </span>

              <div className="flex items-center gap-1 mt-1.5">
                <StatusDot status={step.status} completed={step.completed} />
                {step.label && (
                  <span className="text-[10px] font-medium whitespace-nowrap" style={{ color }}>
                    {step.label}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    );
  }

  if (!steps?.length) return null;

  const WINDOW_SIZE = 5;
  const visibleSteps = steps.slice(overlayWindowStart, overlayWindowStart + WINDOW_SIZE);
  const canSlidePrev = overlayWindowStart > 0;
  const canSlideNext = overlayWindowStart + WINDOW_SIZE < steps.length;

  const cardClass = `fixed top-[75px] !z-50 translate-[10px] left-[40rem] right-[25px] ${
    isDark ? "bg-[#2A2A3D] border-[#3A3A50]" : "bg-white border-[#E5E7EB]"
  } border px-[24px] pt-[25px] pb-[20px] rounded-xl shadow-md shadow-b-2 flex flex-col gap-[6px] overflow-auto`;
  return (
    <div className={cardClass}>
      <div className="flex justify-start">{calendarToggle}</div>

      <div className="flex items-center">
        <button
          type="button"
          onClick={() => canSlidePrev && setOverlayWindowStart((w) => w - 1)}
          disabled={!canSlidePrev}
          className={`flex-shrink-0 flex items-center justify-center w-[20px] h-5 transition-colors ${
            canSlidePrev
              ? isDark
                ? "text-[#9CA3AF] hover:text-[#E5E7EB]"
                : "text-[#6B7280] hover:text-[#374151]"
              : "text-[#D1D5DB] cursor-not-allowed"
          }`}
          title="Previous years"
        >
          <ChevronLeft size={20} strokeWidth={3} />
        </button>

        <div className="flex items-center border-b-2 flex-1">
          {visibleSteps.map((step, index) => {
            const isClickable = step.status !== "LOCKED" || allowLockedClick;
            const isActive = localSelectedStep
              ? step.yearNumber === localSelectedStep.yearNumber
              : step.yearNumber === (selectedYearNumber ?? steps?.[0]?.yearNumber ?? 1);

            return [
              <div
                key={`step-${step.yearNumber}`}
                className={`flex flex-col items-center gap-[3px] flex-shrink-0 min-w-[64px] ${
                  isClickable ? "cursor-pointer" : "cursor-not-allowed"
                }`}
                onClick={() => {
                  if (!isClickable) return;
                  const now = Date.now();
                  const prev = lastClickRef.current;
                  if (prev?.yearNumber === step.yearNumber && now - prev.time < 400) {
                    lastClickRef.current = null;
                    if (phases?.length && onPhaseClick) {
                      const cur = activePhase ?? phases[0];
                      const next = cur === "preCredit" ? "postCredit" : "preCredit";
                      setLocalSelectedPhase(next);
                      onPhaseClick(next);
                    }
                  } else {
                    lastClickRef.current = { yearNumber: step.yearNumber, time: now };
                    setLocalSelectedStep(step);
                    onYearClick?.(step);
                  }
                }}
              >
                <span
                  className={`text-[11px] leading-none font-semibold conthrax-font ${
                    step.status === "CURRENT"
                      ? isDark
                        ? "text-[#93C5FD]"
                        : "text-[#364153]"
                      : step.status === "COMPLETED"
                      ? "text-[#1DA750]"
                      : isDark
                      ? "text-[#9CA3AF]"
                      : "text-[#99A1AF]"
                  }`}
                >
                  {getYearLabel(step)}
                </span>

                <StepIcon status={step.status} completed={step.completed} />

                <div className="flex flex-col items-center">
                  {step.label ? (
                    <span
                      className={`text-[12px] font-medium whitespace-nowrap px-2 py-[2px] rounded-full ${
                        step.status === "CURRENT"
                          ? isDark
                            ? "text-[#93C5FD] font-bold"
                            : "text-[#1E3A5F] font-bold"
                          : step.status === "COMPLETED"
                          ? "bg-[#1DA750] text-white"
                          : isDark
                          ? "bg-[#3A3A50] text-[#9CA3AF]"
                          : "bg-[#E5E7EB] text-[#6B7280]"
                      }`}
                    >
                      {step.label}
                    </span>
                  ) : (
                    <span className="h-[18px]" />
                  )}
                  {isActive ? (
                    <div
                      className={`w-full h-[4px] mt-[6px] ${
                        isDark ? "bg-[#93C5FD]" : "bg-[#1E3A5F]"
                      }`}
                    />
                  ) : (
                    <div className="h-[2px] mt-[2px]" />
                  )}
                </div>
              </div>,

              index < visibleSteps.length - 1 ? (
                <div
                  key={`connector-${step.yearNumber}`}
                  className={`${CONNECTOR_MT} self-start flex-1 min-w-[16px] h-[2px] ${
                    isDark ? "bg-[#3A3A50]" : "bg-[#D1D5DB]"
                  }`}
                />
              ) : null,
            ];
          })}
        </div>

        <button
          type="button"
          onClick={() => canSlideNext && setOverlayWindowStart((w) => w + 1)}
          disabled={!canSlideNext}
          className={`flex-shrink-0 flex items-center justify-center w-[20px] h-5 transition-colors ${
            canSlideNext
              ? isDark
                ? "text-[#9CA3AF] hover:text-[#E5E7EB]"
                : "text-[#6B7280] hover:text-[#374151]"
              : "text-[#D1D5DB] cursor-not-allowed"
          }`}
          title="Next years"
        >
          <ChevronRight size={20} strokeWidth={3} />
        </button>
      </div>
    </div>
  );
};

export default YearStepper;
